import {
    Editor,
    Element,
    Path,
    Range,
    Text,
    type BasePoint,
    type Descendant,
    type Node,
} from "slate";

/**
 * The number of spaces used to indent nested blocks (like nested list items)
 * when serializing the editor as Markdown.
 */
export const INDENT_SPACES = 4;

/**
 * Splits the contents of a Slate editor DOM node into three Markdown strings:
 * - prefix: the contents that come before the selection/cursor
 * - selection: the selected contents, if any, or empty string if just a cursor
 * - suffix: the contents that come after the selection/cursor
 */
export const editorToMarkdownComponents = (
    editor: Editor,
): {
    prefix: string;
    selection: string;
    suffix: string;
} => {
    const selectedRangeStart = editor.selection
        ? Range.start(editor.selection)
        : Editor.end(editor, []);
    const selectedRangeEnd = editor.selection
        ? Range.end(editor.selection)
        : Editor.end(editor, []);

    const {result, selectionStart, selectionEnd} = descendantsToMarkdown(
        editor.children,
        selectedRangeStart,
        selectedRangeEnd,
    );

    // By default, just set everything to prefix, so any insertions will
    // be appended to the very end:
    let prefix = result;
    let selection = "";
    let suffix = "";

    if (isFinite(selectionStart) && isFinite(selectionEnd)) {
        // But in most cases, we should have found the selection/cursor in
        // the document, so we can split the result into parts around it.
        prefix = result.substring(0, selectionStart);
        selection = result.substring(selectionStart, selectionEnd);
        suffix = result.substring(selectionEnd);
    }

    return {prefix, selection, suffix};
};

export function descendantsToMarkdown(
    children: Descendant[],
    selectedRangeStart?: BasePoint,
    selectedRangeEnd?: BasePoint,
) {
    let result = "";
    let selectionStart = Infinity;
    let selectionEnd = -Infinity;

    const listStack: ("bulleted-list" | "numbered-list" | "lettered-list")[] =
        [];

    const visitNode = (node: Node, path: Path) => {
        if (Text.isText(node)) {
            if (node.bold) {
                result += "**";
            }
            if (node.italic) {
                result += "_";
            }
            if (node.underline) {
                result += "__";
            }

            if (
                selectedRangeStart &&
                Path.equals(path, selectedRangeStart.path)
            ) {
                // If this node is where the selection starts, capture the
                // start index so we can split the result string later.
                selectionStart = result.length + selectedRangeStart.offset;
            }

            if (selectedRangeEnd && Path.equals(path, selectedRangeEnd.path)) {
                // If this node is where the selection ends, capture the
                // end index so we can split the result string later.
                selectionEnd = result.length + selectedRangeEnd.offset;
            }

            result += node.text.trimEnd();

            if (node.italic) {
                result += "_";
            }
            if (node.bold) {
                result += "**";
            }
            if (node.underline) {
                result += "__";
            }

            if (node.text.endsWith(" ")) {
                result += " ";
            }
            return;
        }

        if (!Element.isElement(node)) {
            return;
        }

        switch (node.type) {
            case "paragraph": {
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n\n";
                return;
            }

            case "heading-one": {
                result += "# ";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n\n";
                return;
            }

            case "heading-two": {
                result += "## ";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n\n";
                return;
            }

            case "heading-three": {
                result += "### ";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n\n";
                return;
            }

            case "horizontal-rule": {
                result += "---\n\n";
                return;
            }

            case "block-quote": {
                result += "> ";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n\n";
                return;
            }

            case "math-editor":
            case "math-inline": {
                result += `\\(${node.tex}\\)`;
                return;
            }

            case "math-block": {
                result += `\\[\n${node.tex}\n\\]\n\n`;
                return;
            }

            case "code-inline": {
                result += "`";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "`";
                return;
            }

            case "code-block": {
                result += "```" + (node.language || "") + "\n";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "\n```\n\n";
                return;
            }

            case "bulleted-list": {
                listStack.push(node.type);
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                listStack.pop();
                if (!listStack.length) {
                    // If this is a top-level list (no more nesting), add an
                    // extra newline here to end the list block.
                    result += "\n";
                }
                return;
            }

            case "numbered-list": {
                listStack.push(node.type);
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                listStack.pop();
                if (!listStack.length) {
                    // If this is a top-level list (no more nesting), add an
                    // extra newline here to end the list block.
                    result += "\n";
                }
                return;
            }

            case "lettered-list": {
                listStack.push(node.type);
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                listStack.pop();
                if (!listStack.length) {
                    // If this is a top-level list (no more nesting), add an
                    // extra newline here to end the list block.
                    result += "\n";
                }
                return;
            }

            case "list-item": {
                const indent = " ".repeat(
                    INDENT_SPACES * (listStack.length - 1),
                );
                const listType = listStack.at(-1)!;
                const indexWithinList = path.at(-1)! || 0;

                if (listType === "numbered-list") {
                    const number = indexWithinList + 1;
                    result += indent + `${number}. `;
                } else if (listType === "lettered-list") {
                    const charIndex = Math.min(26, indexWithinList);
                    const letter = String.fromCharCode(97 + charIndex);
                    result += indent + `${letter}. `;
                } else {
                    result += indent + "- ";
                }

                if (node.children.length > 0) {
                    visitNode(node.children[0], [...path, 0]);
                    result += "\n";
                }

                if (node.children.length > 1) {
                    // If there's a nested list, visit it recursively:
                    visitNode(node.children[1]!, [...path, 1]);
                }
                return;
            }

            case "list-item-text": {
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                return;
            }

            case "link": {
                result += "[";
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                result += "](";
                result += node.url;
                result += ")";
                return;
            }

            case "image": {
                result += "![";
                result += node.alt;
                result += "](";
                result += node.url;
                result += ")";
                return;
            }

            case "table": {
                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );
                // only one \n, since table-rows add a single \n already
                result += "\n";
                return;
            }

            case "table-body": {
                node.children.forEach((child, i) => {
                    visitNode(child, [...path, i]);
                });
                return;
            }

            case "table-header": {
                node.children.forEach((child, i) => {
                    visitNode(child, [...path, i]);
                });
                const columnCount =
                    node.children.length && node.children[0].children.length;
                if (columnCount) {
                    result +=
                        "|" +
                        Array.from({length: columnCount})
                            .map(() => " --- ")
                            .join("|") +
                        "|\n";
                }
                return;
            }

            case "table-row": {
                node.children.forEach((child, i) => {
                    result += "| ";
                    visitNode(child, [...path, i]);
                    result += " ";
                });
                result += "|\n";
                return;
            }

            case "table-cell": {
                const resultBeforeCell = result;

                node.children.forEach((child, i) =>
                    visitNode(child, [...path, i]),
                );

                // NOTE(reid): Table cells may contain newlines, like in the
                // case of rubrics, but we want to replace raw newline symbols
                // with "<br />" in the saved Markdown. Without this, table
                // cells containing newlines get split across lines in the
                // saved document.
                const cellText = result
                    .substring(resultBeforeCell.length)
                    .replaceAll("\n", "<br />");
                result = resultBeforeCell + cellText;
                return;
            }
        }
    };

    // Kick off the recursion by visiting the editor root's children:
    children.forEach((child, i) => visitNode(child, [i]));

    return {
        result,
        selectionStart,
        selectionEnd,
    };
}
